import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import './styles/wandero-tokens.css';
import './styles/bulk-translate.css';

import { StrictMode, lazy, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { MantineProvider, ColorSchemeScript, Center, Loader } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { ModalsProvider } from '@mantine/modals';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { theme, cssVariablesResolver } from '@/theme';
import { enableMocks } from '@/mocks/init';
import { AuthProvider } from '@/auth/auth-context';
import { LangProvider } from '@/lib/lang-context';
import { LoginPage } from '@/auth/login-page';
import { CallbackPage } from '@/auth/callback-page';
import { RequireAuth, RequireRole } from '@/auth/guard';
import { AppLayout } from '@/components/AppLayout';

// Home dashboard — lazy loaded (rendered at both `/` and `/dashboard`)
const DashboardPage = lazy(() =>
  import('@/pages/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);

// Catalog pages — lazy loaded
const CategoriesPage = lazy(() =>
  import('@/pages/categories/CategoriesPage').then((m) => ({ default: m.CategoriesPage })),
);
const WpsListPage = lazy(() =>
  import('@/pages/wallpapers/WpsListPage').then((m) => ({ default: m.WpsListPage })),
);
const WpEditPage = lazy(() =>
  import('@/pages/wallpapers/WpEditPage').then((m) => ({ default: m.WpEditPage })),
);

// Events page — lazy loaded (R-8)
const EventsPage = lazy(() =>
  import('@/pages/admin/EventsPage').then((m) => ({ default: m.EventsPage })),
);

// AI compare page — temporary A/B harness for Haiku vs Opus
const AiComparePage = lazy(() =>
  import('@/pages/admin/AiComparePage').then((m) => ({ default: m.AiComparePage })),
);

// Eval matrix page — /settings/eval-matrix
const EvalMatrixPage = lazy(() =>
  import('@/pages/eval/EvalMatrixPage').then((m) => ({ default: m.EvalMatrixPage })),
);

// App settings page — A6 §7.3
const AppSettingsPage = lazy(() =>
  import('@/pages/admin/AppSettingsPage').then((m) => ({ default: m.AppSettingsPage })),
);

// Ideas registry — read-only RUN/DONE/HOLD board over ideahub
// (idea 2026-08-18-ideas-widget-v3-status-groups).
const IdeasPage = lazy(() =>
  import('@/pages/ideas/IdeasPage').then((m) => ({ default: m.IdeasPage })),
);

// Orders page — T7 (orders-store spec)
const OrdersPage = lazy(() =>
  import('@/pages/orders/OrdersPage').then((m) => ({ default: m.OrdersPage })),
);

// Settings pages — lazy loaded (T24)
const SettingsLayout = lazy(() =>
  import('@/pages/settings/SettingsLayout').then((m) => ({ default: m.SettingsLayout })),
);
const MaterialsPage = lazy(() =>
  import('@/pages/settings/MaterialsPage').then((m) => ({ default: m.MaterialsPage })),
);
const MaterialEditPage = lazy(() =>
  import('@/pages/settings/MaterialEditPage').then((m) => ({ default: m.MaterialEditPage })),
);
const MaterialsPricesPage = lazy(() =>
  import('@/pages/settings/MaterialsPricesPage').then((m) => ({ default: m.MaterialsPricesPage })),
);
// Discount engine pages — T6
const DiscountSchedulesPage = lazy(() =>
  import('@/pages/settings/DiscountSchedulesPage').then((m) => ({ default: m.DiscountSchedulesPage })),
);
const CustomerDiscountsPage = lazy(() =>
  import('@/pages/settings/CustomerDiscountsPage').then((m) => ({ default: m.CustomerDiscountsPage })),
);
// Discount audit page — T7
const DiscountAuditPage = lazy(() =>
  import('@/pages/settings/DiscountAuditPage').then((m) => ({ default: m.DiscountAuditPage })),
);
const SitesPage = lazy(() =>
  import('@/pages/settings/SitesPage').then((m) => ({ default: m.SitesPage })),
);
const LanguagesPage = lazy(() =>
  import('@/pages/settings/LanguagesPage').then((m) => ({ default: m.LanguagesPage })),
);
const AiModelsPage = lazy(() =>
  import('@/pages/settings/AiModelsPage').then((m) => ({ default: m.AiModelsPage })),
);
const PromptsPage = lazy(() =>
  import('@/pages/settings/PromptsPage').then((m) => ({ default: m.PromptsPage })),
);

// Blog pages — T4 (composes the TipTap editor (T3), CRUD/upload data layer
// (T5), and translate module (T9) into BlogListPage/BlogEditPage).
const BlogListPage = lazy(() =>
  import('@/pages/blog/BlogListPage').then((m) => ({ default: m.BlogListPage })),
);
const BlogEditPage = lazy(() =>
  import('@/pages/blog/BlogEditPage').then((m) => ({ default: m.BlogEditPage })),
);

// Customer reviews page — T2 (spec 2026-07-12-wandero-customer-photo-reviews-gallery).
// ONE route with an optional param: `/customer-reviews/:id?` — the ListPage
// opens ReviewDrawer over the list when `:id` is present ('new' or a numeric
// id, ReviewDrawer's own isNew convention). Same element for list and item, so
// opening/closing the drawer never remounts the list.
const CustomerReviewsListPage = lazy(() =>
  import('@/pages/customer-reviews/ListPage').then((m) => ({ default: m.ListPage })),
);

// Interior templates — T6 (spec 2026-07-17-interior-templates-cutout).
// Upload PNG with transparent cutout → auto-detected quad → manual corner
// editing. Feeds the wallpaper-page mockup composer (TemplateMockupsPanel, T7).
const InteriorTemplatesPage = lazy(() =>
  import('@/pages/interior-templates/TemplatesListPage').then((m) => ({
    default: m.TemplatesListPage,
  })),
);

// ---------------------------------------------------------------------------
// TanStack Query
// ---------------------------------------------------------------------------
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      retry: 1,
    },
  },
});

// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------
const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('Root element #root not found in index.html');
}

// Activate MSW before rendering so all fetches are intercepted from the start.
// enableMocks() is a no-op when VITE_USE_MOCKS !== 'true'.
await enableMocks();

createRoot(rootEl).render(
  <StrictMode>
    {/* ColorSchemeScript must be outside MantineProvider for SSR — fine in SPA too */}
    <ColorSchemeScript defaultColorScheme="light" />
    <QueryClientProvider client={queryClient}>
      <MantineProvider
        theme={theme}
        defaultColorScheme="light"
        cssVariablesResolver={cssVariablesResolver}
      >
        <ModalsProvider>
          <Notifications position="top-right" />
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <AuthProvider>
                <LangProvider>
                <Suspense
                  fallback={
                    <Center h="100vh">
                      <Loader aria-label="Loading page..." />
                    </Center>
                  }
                >
                  <Routes>
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/auth/callback" element={<CallbackPage />} />
                    <Route
                      element={
                        <RequireAuth>
                          <AppLayout />
                        </RequireAuth>
                      }
                    >
                      <Route
                        path="/"
                        element={
                          <RequireRole allow={['admin', 'content', 'smm']}>
                            <DashboardPage />
                          </RequireRole>
                        }
                      />
                      <Route
                        path="/dashboard"
                        element={
                          <RequireRole allow={['admin', 'content', 'smm']}>
                            <DashboardPage />
                          </RequireRole>
                        }
                      />
                      {/* Catalog routes */}
                      <Route
                        path="/categories"
                        element={
                          <RequireRole allow={['admin', 'content']}>
                            <CategoriesPage />
                          </RequireRole>
                        }
                      />
                      <Route
                        path="/wallpapers"
                        element={
                          <RequireRole allow={['admin', 'content']}>
                            <WpsListPage />
                          </RequireRole>
                        }
                      />
                      <Route
                        path="/wallpapers/:idWp"
                        element={
                          <RequireRole allow={['admin', 'content']}>
                            <WpEditPage />
                          </RequireRole>
                        }
                      />
                      {/* Blog routes — same shape as /wallpapers + /wallpapers/:idWp.
                          `:idPost` also matches the literal "new" segment
                          (BlogEditPage's own isNew convention, mirrors
                          CategoryEditPage). */}
                      <Route
                        path="/blog"
                        element={
                          <RequireRole allow={['admin', 'content']}>
                            <BlogListPage />
                          </RequireRole>
                        }
                      />
                      <Route
                        path="/blog/:idPost"
                        element={
                          <RequireRole allow={['admin', 'content']}>
                            <BlogEditPage />
                          </RequireRole>
                        }
                      />
                      {/* Customer reviews — one optional-param route; ':id?'
                          ('new' | numeric) opens ReviewDrawer over the list. */}
                      <Route
                        path="/customer-reviews/:id?"
                        element={
                          <RequireRole allow={['admin', 'content']}>
                            <CustomerReviewsListPage />
                          </RequireRole>
                        }
                      />
                      {/* Interior templates — T6 (interior-templates-cutout) */}
                      <Route
                        path="/interior-templates"
                        element={
                          <RequireRole allow={['admin', 'content']}>
                            <InteriorTemplatesPage />
                          </RequireRole>
                        }
                      />
                      {/* Ideas registry — read-only (admin only) */}
                      <Route
                        path="/ideas"
                        element={
                          <RequireRole allow={['admin']}>
                            <IdeasPage />
                          </RequireRole>
                        }
                      />
                      {/* Orders route — T7 (admin only) */}
                      <Route
                        path="/orders"
                        element={
                          <RequireRole allow={['admin']}>
                            <OrdersPage />
                          </RequireRole>
                        }
                      />
                      {/* Settings routes — T24 (admin only) */}
                      <Route
                        path="/settings"
                        element={
                          <RequireRole allow={['admin']}>
                            <SettingsLayout />
                          </RequireRole>
                        }
                      >
                        <Route index element={<Navigate to="materials" replace />} />
                        <Route path="materials" element={<MaterialsPage />} />
                        <Route path="materials/:id" element={<MaterialEditPage />} />
                        <Route path="prices" element={<MaterialsPricesPage />} />
                        <Route path="discount-schedules" element={<DiscountSchedulesPage />} />
                        <Route path="customer-discounts" element={<CustomerDiscountsPage />} />
                        <Route path="discount-audit" element={<DiscountAuditPage />} />
                        <Route path="sites" element={<SitesPage />} />
                        <Route path="languages" element={<LanguagesPage />} />
                        <Route path="ai" element={<AiModelsPage />} />
                        <Route path="prompts" element={<PromptsPage />} />
                        <Route path="events" element={<EventsPage />} />
                        <Route path="ai-compare" element={<AiComparePage />} />
                        <Route path="app-settings" element={<AppSettingsPage />} />
                        <Route
                          path="eval-matrix"
                          element={
                            <RequireRole allow={['admin']}>
                              <EvalMatrixPage />
                            </RequireRole>
                          }
                        />
                        <Route path="*" element={<Navigate to="materials" replace />} />
                      </Route>
                      {/* AI prompt management lives inline on the wallpaper
                          editor (Prompt button per step). The standalone
                          /admin/ai/prompts pages were removed. */}
                      {/* Legacy redirects — admin paths moved to /settings/* */}
                      <Route path="/admin/events" element={<Navigate to="/settings/events" replace />} />
                      <Route path="/admin/ai-compare" element={<Navigate to="/settings/ai-compare" replace />} />
                      <Route path="/admin/app-settings" element={<Navigate to="/settings/app-settings" replace />} />
                      <Route path="/admin/*" element={<DashboardPage />} />
                    </Route>
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Suspense>
                </LangProvider>
              </AuthProvider>
            </BrowserRouter>
        </ModalsProvider>
      </MantineProvider>
    </QueryClientProvider>
  </StrictMode>,
);
